import React, { useState, useEffect, useRef, useMemo } from "react";
import FormInput from '../Input'
import BankingService from '../../api/bank-service'
import { ChevronDown, Spinner } from '../../assets/svgs/icons-svg'
import { BankDetails } from '../../interfaces/bank'



const IDENTITY_TYPES = ['KYB', 'KYC']

// ── Step 1: Bank Details Form ─────────────────────────────────────────────────
export function BankDetailsStep({ onVerify }: { onVerify: (d: BankDetails) => void }) {
  const [bankCode, setBankCode] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [accountName, setAccountName] = useState('')
  const [identityType, setIdentityType] = useState('')
  const [verifyingAccount, setVerifyingAccount] = useState(false)
  const [verifyError, setVerifyError] = useState('')
  const [isBankDropdownOpen, setIsBankDropdownOpen] = useState(false)
  const [bankSearchTerm, setBankSearchTerm] = useState('')
  const bankDropdownRef = useRef<HTMLDivElement>(null)

  // ── GET /banking/list-banks ───────────────────────────────────────────────
  const { data: banks, isLoading: loadingBanks, error: banksError } = BankingService.useGetBanks()

  const sortedBanks = useMemo(
    () => (banks ? [...banks].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [banks]
  )
  const filteredBanks = useMemo(
    () =>
      sortedBanks.filter((b) => b.name.toLowerCase().includes(bankSearchTerm.toLowerCase())),
    [sortedBanks, bankSearchTerm]
  )

  const selectedBank = banks?.find((b) => b.code === bankCode)
  const canLookup = bankCode && accountNumber.length === 10

  // ── Close the bank dropdown on outside click ──────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(event.target as Node)) {
        setIsBankDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectBank = (code: string) => {
    setBankCode(code)
    setAccountName('')
    setVerifyError('')
    setBankSearchTerm('')
    setIsBankDropdownOpen(false)
  }

  // ── POST /banking/verify-account-number — auto-fires on valid bank + acct ─
  useEffect(() => {
    if (!canLookup) {
      setAccountName('')
      setVerifyError('')
      return
    }

  const lookup = async () => {
      try {
        setVerifyingAccount(true)
        setVerifyError('')
        setAccountName('')
        const res = await BankingService.verifyAccountNumber({ accountNumber, bankCode })

        const isValid = res.isValid ?? res.status?.isValid
        if (!isValid || !res.accountName) {
          setVerifyError('Account not found. Please check the account number.')
          return
        }

        setAccountName(res.accountName)
      } catch {
        setVerifyError('Could not verify account. Check the number and try again.')
      } finally {
        setVerifyingAccount(false)
      }
    }

    lookup()
  }, [bankCode, accountNumber, canLookup])

  const isValid = !!(bankCode && accountNumber && accountName && identityType && !verifyingAccount)

  const handleSubmit = () => {
    if (!isValid || !selectedBank) return
    onVerify({
      bankName: selectedBank.name,
      bankCode,
      accountNumber,
      accountName,
      identityType,
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Bank Account Verification{' '}
          <span className="font-normal text-gray-500">[200,000 Max]</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1 italic">
          "Payments will be sent directly to this account."
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Bank Name */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Bank Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            {loadingBanks ? (
              <div className="w-full border border-gray-200 rounded-lg px-3 py-3.5 text-sm text-gray-400 flex items-center gap-2">
                <Spinner className="w-4 h-4 text-gray-400" /> Loading banks...
              </div>
            ) : banksError ? (
              <p className="text-xs text-red-500">Could not load banks. Please refresh.</p>
            ) : (
              <div ref={bankDropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setIsBankDropdownOpen((open) => !open)}
                  className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-3 py-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-300 transition-all text-left"
                  aria-haspopup="listbox"
                  aria-expanded={isBankDropdownOpen}
                >
                  <span className={selectedBank ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedBank ? selectedBank.name : 'Select bank'}
                  </span>
                  <ChevronDown />
                </button>

                {isBankDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        autoFocus
                        type="text"
                        value={bankSearchTerm}
                        onChange={(e) => setBankSearchTerm(e.target.value)}
                        placeholder="Search bank..."
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                      />
                    </div>
                    <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
                      {filteredBanks.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-gray-400">No banks found</li>
                      ) : (
                        filteredBanks.map((b) => (
                          <li key={b.code} role="option" aria-selected={b.code === bankCode}>
                            <button
                              type="button"
                              onClick={() => handleSelectBank(b.code)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                                b.code === bankCode
                                  ? 'bg-gray-50 font-medium text-gray-900'
                                  : 'text-gray-700'
                              }`}
                            >
                              {b.name}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Account Number */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Account Number <span className="text-red-500">*</span>
          </label>
          <FormInput
            placeholder="213454678"
            size="lg"
            value={accountNumber}
            onChange={(e) => {
              setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
              setVerifyError('')
            }}
            type="text"
            name="accountNumber"
          />
        </div>

        {/* Account Name — read-only, auto-populated via API */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Account Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <FormInput
              placeholder={verifyingAccount ? 'Verifying...' : 'Auto-filled after verification'}
              size="lg"
              value={accountName}
              onChange={() => {}}
              type="text"
              name="accountName"
              disabled
            />
            {verifyingAccount && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                <Spinner className="w-4 h-4 text-gray-400" />
              </span>
            )}
          </div>
          {verifyError && <p className="text-xs text-red-500 mt-1">{verifyError}</p>}
        </div>

        {/* Identity Type */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">
            Identity Type <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={identityType}
              onChange={(e) => setIdentityType(e.target.value)}
              className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-300 transition-all pr-8"
            >
              <option value="">select type</option>
              {IDENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <ChevronDown />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          disabled={!isValid}
          onClick={handleSubmit}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            isValid
              ? 'bg-gray-900 text-white hover:bg-gray-700 active:scale-95 cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Verify
        </button>
      </div>
    </div>
  )
}